/**
 * A space sheet, live: the grid (`SheetGrid`) over the space's real-time
 * document (`useSpaceDocument(spaceId, "sheet:<uuid>")`), with who else is
 * here. Like the Board it waits for the first sync before it paints and
 * says something only when the sheet is not simply up to date.
 *
 * `LiveSheetView` is the part below the hook, so a browser fixture can drive
 * it with its own provider.
 *
 * @module components/spaces/sheet/SpaceSheetLive
 */
import type { Awareness } from "y-protocols/awareness";
import type * as Y from "yjs";

import type { SpaceDocumentStatus } from "../../../spaces/realtime/SpaceDocumentProvider";
import { useSpaceDocument } from "../../../spaces/realtime/useSpaceDocument";
import { StatusNote, useEverSynced } from "../board/SpaceBoardLive";
import { PresenceStack, useDocumentPresence } from "../DocumentPresence";
import { SheetGrid } from "./SheetGrid";

export function LiveSheetView({
  docId,
  doc,
  awareness,
  status,
  access,
}: {
  readonly docId: string;
  readonly doc: Y.Doc | null;
  readonly awareness: Awareness | null;
  readonly status: SpaceDocumentStatus;
  readonly access: "edit" | "view" | null;
}) {
  const presence = useDocumentPresence(awareness);
  const everSynced = useEverSynced(status, doc);
  const ready = doc !== null && awareness !== null && everSynced;
  const editable = access === "edit" && status !== "closed";
  return (
    <section aria-label="Sheet" data-space-sheet={docId} className="w-full">
      {status !== "synced" || access === "view" || presence.length > 0 ? (
        <div className="mb-3 flex min-h-6 items-center justify-between gap-3">
          <StatusNote status={status} access={access} noun="sheet" />
          <PresenceStack entries={presence} />
        </div>
      ) : null}
      {ready ? (
        <SheetGrid doc={doc} awareness={awareness} editable={editable} />
      ) : (
        <div className="h-[min(64vh,640px)] animate-pulse rounded-lg border border-border bg-muted/40" aria-hidden="true" />
      )}
    </section>
  );
}

export function SpaceSheetLive({ spaceId, docId }: { readonly spaceId: string; readonly docId: string }) {
  const { doc, awareness, status, access } = useSpaceDocument(spaceId, docId);
  return <LiveSheetView docId={docId} doc={doc} awareness={awareness} status={status} access={access} />;
}
